import { ArrowLeft, Calculator, DollarSign, Plus, Save, TestTube, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePricingConfiguratorData } from "@/hooks/pricing-configurator/usePricingConfiguratorData";
import { usePricingConfiguratorLogic } from "@/hooks/pricing-configurator/usePricingConfiguratorLogic";
import { usePricingConfiguratorMutations } from "@/hooks/pricing-configurator/usePricingConfiguratorMutations";
import { usePricingConfiguratorState } from "@/hooks/pricing-configurator/usePricingConfiguratorState";
import { PricingService } from "@/services/PricingService";
import type { PricingType } from "@/types/components";

interface PricingConfiguratorProps {
  templateId?: number | null;
  onClose: () => void;
}

const PricingConfigurator = ({ templateId, onClose }: PricingConfiguratorProps) => {
  // Data fetching
  const { templateData, isLoading } = usePricingConfiguratorData({ templateId });

  // State management
  const {
    template,
    selectedComponent,
    testCases,
    showPreviewDialog,
    previewResults,
    setSelectedComponent,
    setShowPreviewDialog,
    setPreviewResults,
    getAllComponents,
    updateComponentPricing,
    addComponentOption,
    updateComponentOption,
    removeComponentOption,
  } = usePricingConfiguratorState({ templateData });

  // Mutations
  const { savePricingMutation } = usePricingConfiguratorMutations({
    templateId,
    template,
  });

  // Business logic
  const { testPricing, validateFormula, savePricingConfiguration } = usePricingConfiguratorLogic({
    getAllComponents,
    testCases,
    setPreviewResults,
    setShowPreviewDialog,
    savePricingMutation,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading template...</div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Template not found</div>
      </div>
    );
  }

  const allComponents = getAllComponents();

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onClose}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Templates
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Pricing Configurator</h1>
            <p className="text-gray-600 mt-1">Configure pricing for template: {template.name}</p>
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={testPricing}>
            <TestTube className="w-4 h-4 mr-2" />
            Test Pricing
          </Button>
          <Button
            onClick={() => savePricingConfiguration(template)}
            disabled={savePricingMutation.isPending}
          >
            <Save className="w-4 h-4 mr-2" />
            {savePricingMutation.isPending ? "Saving..." : "Save Configuration"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Component List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Components ({allComponents.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {allComponents.map((component, index) => (
                  <div
                    key={component.id || index}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedComponent?.id === component.id
                        ? "bg-blue-100 border-blue-500 border"
                        : "bg-gray-50 hover:bg-gray-100"
                    }`}
                    onClick={() => setSelectedComponent(component)}
                  >
                    <div className="font-medium text-sm">{component.label}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      <Badge variant="outline" className="text-xs mr-2">
                        {component.component_type}
                      </Badge>
                      {component.pricing_type && (
                        <Badge variant="secondary" className="text-xs">
                          {component.pricing_type}
                        </Badge>
                      )}
                    </div>
                    {component.base_price && component.base_price > 0 && (
                      <div className="text-sm font-medium text-green-600 mt-1">
                        ${component.base_price.toFixed(2)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pricing Configuration */}
        <div className="lg:col-span-3">
          {selectedComponent ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Configure Pricing: {selectedComponent.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="basic" className="w-full">
                  <TabsList>
                    <TabsTrigger value="basic">Basic Pricing</TabsTrigger>
                    <TabsTrigger value="options">Options & Modifiers</TabsTrigger>
                    <TabsTrigger value="quantity">Quantity Settings</TabsTrigger>
                    <TabsTrigger value="advanced">Advanced</TabsTrigger>
                  </TabsList>

                  <TabsContent value="basic" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="base-price">Base Price ($)</Label>
                        <Input
                          id="base-price"
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.01"
                          value={selectedComponent.base_price || ""}
                          onChange={(e) => {
                            const newPrice = Number.parseFloat(e.target.value) || 0;
                            updateComponentPricing(selectedComponent.id || 0, {
                              base_price: newPrice,
                            });
                            setSelectedComponent((prev) =>
                              prev ? { ...prev, base_price: newPrice } : null
                            );
                          }}
                          placeholder="0.00"
                        />
                      </div>

                      <div>
                        <Label htmlFor="pricing-type">Pricing Type</Label>
                        <Select
                          value={selectedComponent.pricing_type || "fixed"}
                          onValueChange={(value: PricingType) => {
                            updateComponentPricing(selectedComponent.id || 0, {
                              pricing_type: value,
                            });
                            setSelectedComponent((prev) =>
                              prev ? { ...prev, pricing_type: value } : null
                            );
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fixed">Fixed Price</SelectItem>
                            <SelectItem value="per_unit">Per Unit</SelectItem>
                            <SelectItem value="per_sqft">Per Square Foot</SelectItem>
                            <SelectItem value="per_linear">Per Linear Foot</SelectItem>
                            <SelectItem value="formula">Custom Formula</SelectItem>
                            <SelectItem value="none">No Pricing</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {selectedComponent.pricing_type === "per_unit" && (
                      <div>
                        <Label htmlFor="unit-label">Unit Label</Label>
                        <Input
                          id="unit-label"
                          value={selectedComponent.unit_label || ""}
                          onChange={(e) => {
                            updateComponentPricing(selectedComponent.id || 0, {
                              unit_label: e.target.value,
                            });
                            setSelectedComponent((prev) =>
                              prev ? { ...prev, unit_label: e.target.value } : null
                            );
                          }}
                          placeholder="e.g., units, pieces, items"
                        />
                      </div>
                    )}

                    {selectedComponent.pricing_type === "formula" && (
                      <div>
                        <Label htmlFor="formula">Custom Formula</Label>
                        <Input
                          id="formula"
                          value={selectedComponent.formula || ""}
                          onChange={(e) => {
                            updateComponentPricing(selectedComponent.id || 0, {
                              formula: e.target.value,
                            });
                            setSelectedComponent((prev) =>
                              prev ? { ...prev, formula: e.target.value } : null
                            );
                          }}
                          placeholder="e.g., quantity * base_price * 1.2"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Available variables: quantity, base_price, price
                        </p>
                        {selectedComponent.formula && (
                          <div className="mt-2 p-2 bg-gray-50 rounded">
                            <p className="text-sm font-medium">Formula Test:</p>
                            <p className="text-sm">
                              Input: quantity=10, base_price=${selectedComponent.base_price || 0} →
                              Result: $
                              {PricingService.evaluateFormula(
                                selectedComponent.formula,
                                10,
                                selectedComponent.base_price || 0
                              ).toFixed(2)}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="options" className="space-y-4">
                    {["select", "radio", "checkbox"].includes(selectedComponent.component_type) ? (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <Label>Component Options</Label>
                          <Button
                            size="sm"
                            onClick={() => addComponentOption(selectedComponent.id || 0)}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Add Option
                          </Button>
                        </div>

                        <div className="space-y-3">
                          {selectedComponent.options?.map((option, index) => (
                            <Card key={index} className="p-4">
                              <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                  <Label htmlFor={`option-value-${index}`}>Option Value</Label>
                                  <Input
                                    id={`option-value-${index}`}
                                    value={option.option_value}
                                    onChange={(e) =>
                                      updateComponentOption(selectedComponent.id || 0, index, {
                                        option_value: e.target.value,
                                      })
                                    }
                                    placeholder="option_value"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor={`option-label-${index}`}>Display Label</Label>
                                  <Input
                                    id={`option-label-${index}`}
                                    value={option.option_label}
                                    onChange={(e) =>
                                      updateComponentOption(selectedComponent.id || 0, index, {
                                        option_label: e.target.value,
                                      })
                                    }
                                    placeholder="Display Label"
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-3 gap-3">
                                <div>
                                  <Label htmlFor={`price-modifier-${index}`}>Price Modifier</Label>
                                  <Input
                                    id={`price-modifier-${index}`}
                                    type="number"
                                    inputMode="decimal"
                                    step="0.01"
                                    value={option.price_modifier || ""}
                                    onChange={(e) =>
                                      updateComponentOption(selectedComponent.id || 0, index, {
                                        price_modifier: Number.parseFloat(e.target.value) || 0,
                                      })
                                    }
                                    placeholder="0.00"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor={`modifier-type-${index}`}>Modifier Type</Label>
                                  <Select
                                    value={option.price_modifier_type || "add"}
                                    onValueChange={(value: any) =>
                                      updateComponentOption(selectedComponent.id || 0, index, {
                                        price_modifier_type: value,
                                      })
                                    }
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="add">Add</SelectItem>
                                      <SelectItem value="multiply">Multiply</SelectItem>
                                      <SelectItem value="percentage">Percentage</SelectItem>
                                      <SelectItem value="replace">Replace</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="flex items-end">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      removeComponentOption(selectedComponent.id || 0, index)
                                    }
                                    className="text-red-600"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            </Card>
                          )) || []}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        Options are only available for select, radio, and checkbox components
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="quantity" className="space-y-4">
                    <div className="flex items-center space-x-2 mb-4">
                      <Switch
                        id="allow-quantity"
                        checked={selectedComponent.allow_quantity_input}
                        onCheckedChange={(checked) => {
                          updateComponentPricing(selectedComponent.id || 0, {
                            allow_quantity_input: checked,
                          });
                          setSelectedComponent((prev) =>
                            prev ? { ...prev, allow_quantity_input: checked } : null
                          );
                        }}
                      />
                      <Label htmlFor="allow-quantity">Allow Quantity Input</Label>
                    </div>

                    {selectedComponent.allow_quantity_input && (
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="min-quantity">Minimum Quantity</Label>
                          <Input
                            id="min-quantity"
                            type="number"
                            inputMode="decimal"
                            min="0"
                            value={selectedComponent.min_quantity || ""}
                            onChange={(e) => {
                              const value = Number.parseInt(e.target.value, 10) || undefined;
                              updateComponentPricing(selectedComponent.id || 0, {
                                min_quantity: value,
                              });
                              setSelectedComponent((prev) =>
                                prev ? { ...prev, min_quantity: value } : null
                              );
                            }}
                            placeholder="No minimum"
                          />
                        </div>
                        <div>
                          <Label htmlFor="max-quantity">Maximum Quantity</Label>
                          <Input
                            id="max-quantity"
                            type="number"
                            inputMode="decimal"
                            min="0"
                            value={selectedComponent.max_quantity || ""}
                            onChange={(e) => {
                              const value = Number.parseInt(e.target.value, 10) || undefined;
                              updateComponentPricing(selectedComponent.id || 0, {
                                max_quantity: value,
                              });
                              setSelectedComponent((prev) =>
                                prev ? { ...prev, max_quantity: value } : null
                              );
                            }}
                            placeholder="No maximum"
                          />
                        </div>
                        <div>
                          <Label htmlFor="default-quantity">Default Quantity</Label>
                          <Input
                            id="default-quantity"
                            type="number"
                            inputMode="decimal"
                            min="1"
                            value={selectedComponent.default_quantity || ""}
                            onChange={(e) => {
                              const value = Number.parseInt(e.target.value, 10) || 1;
                              updateComponentPricing(selectedComponent.id || 0, {
                                default_quantity: value,
                              });
                              setSelectedComponent((prev) =>
                                prev ? { ...prev, default_quantity: value } : null
                              );
                            }}
                            placeholder="1"
                          />
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="advanced" className="space-y-4">
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="allow-photo-upload"
                          checked={selectedComponent.allow_photo_upload}
                          onCheckedChange={(checked) => {
                            updateComponentPricing(selectedComponent.id || 0, {
                              allow_photo_upload: checked,
                            });
                            setSelectedComponent((prev) =>
                              prev ? { ...prev, allow_photo_upload: checked } : null
                            );
                          }}
                        />
                        <Label htmlFor="allow-photo-upload">Allow Photo Upload</Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Switch
                          id="show-running-total"
                          checked={selectedComponent.show_running_total}
                          onCheckedChange={(checked) => {
                            updateComponentPricing(selectedComponent.id || 0, {
                              show_running_total: checked,
                            });
                            setSelectedComponent((prev) =>
                              prev ? { ...prev, show_running_total: checked } : null
                            );
                          }}
                        />
                        <Label htmlFor="show-running-total">Show in Running Total</Label>
                      </div>

                      <Separator />

                      <div>
                        <Label>Pricing Validation</Label>
                        <div className="mt-2 p-3 bg-gray-50 rounded">
                          {PricingService.validatePricing(selectedComponent).length === 0 ? (
                            <p className="text-sm text-green-600">
                              ✅ Pricing configuration is valid
                            </p>
                          ) : (
                            <div className="text-sm text-red-600">
                              <p className="font-medium">⚠️ Pricing issues found:</p>
                              <ul className="mt-1 ml-4 list-disc">
                                {PricingService.validatePricing(selectedComponent).map(
                                  (error, index) => (
                                    <li key={index}>{error}</li>
                                  )
                                )}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <div className="flex items-center justify-center h-64">
              <p className="text-gray-500">Select a component to configure its pricing</p>
            </div>
          )}
        </div>
      </div>

      {/* Preview Results Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Pricing Test Results
            </DialogTitle>
            <DialogDescription>Test results for all components in this template</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {previewResults.map((result, index) => (
              <Card key={index} className={result.success ? "border-green-200" : "border-red-200"}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between text-lg">
                    <div className="flex items-center gap-2">
                      {result.success ? "✅" : "❌"}
                      {result.component.label}
                      <Badge variant="outline">{result.component.component_type}</Badge>
                    </div>
                    {result.success && (
                      <div className="text-green-600 font-bold">
                        {PricingService.formatPrice(result.breakdown.calculated_price)}
                      </div>
                    )}
                  </CardTitle>
                </CardHeader>

                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p>
                        <strong>Test Value:</strong> {JSON.stringify(result.testCase.test_value)}
                      </p>
                      {result.testCase.test_quantity && (
                        <p>
                          <strong>Test Quantity:</strong> {result.testCase.test_quantity}
                        </p>
                      )}
                      <p>
                        <strong>Base Price:</strong> ${result.component.base_price || 0}
                      </p>
                      <p>
                        <strong>Pricing Type:</strong> {result.component.pricing_type || "fixed"}
                      </p>
                    </div>

                    <div>
                      {result.success ? (
                        <div>
                          <p>
                            <strong>Calculated Price:</strong>{" "}
                            {PricingService.formatPrice(result.breakdown.calculated_price)}
                          </p>
                          <p>
                            <strong>Calculation Method:</strong>{" "}
                            {result.breakdown.calculation_method}
                          </p>
                          {result.breakdown.details && (
                            <p>
                              <strong>Details:</strong> {result.breakdown.details}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="text-red-600">
                          <p>
                            <strong>Error:</strong> {result.error}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PricingConfigurator;
